import { Voip } from '@brighterly/lib-core-types/support/enums'

import { useVoipRoomStore } from '@pages/discipline/classroom'
import { useVoipRoomTokenApi } from '@pages/discipline/classroom'
import { useMiddlewareOptionsReady } from '@app/guards/options-ready.hook'
import { useMiddlewareBookingVoip } from '@app/guards/booking-voip.hook'
import { useToast } from '@shared/lib/toast'
import { useOptionsStore } from '@shared/model'
import { useLessonRoomStore } from '@pages/discipline/classroom'
import { flushBeforeLeave } from '@shared/lib/amplitude'

import { abortNavigation, defineNuxtRouteMiddleware, navigateTo } from '#app'

const LESSON_ROOM_UNAVAILABLE = {
  title: 'Error',
  description: 'Unable to open the lesson room. Please contact support.',
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  const lessonRoomStore = useLessonRoomStore()
  const optionsStore = useOptionsStore()
  const voipRoomStore = useVoipRoomStore()

  voipRoomStore.setBookingVoip(null)

  const bookingId = Number(to.params.booking)

  if (!bookingId) {
    console.warn('missing bookingId for middleware/voip-room.ts')
    return navigateTo('/')
  }

  const booking = lessonRoomStore.getBooking
  const externalLessonUrl = booking?.lesson_url ?? null

  const toExternalLesson = () => {
    if (!externalLessonUrl) return navigateTo('/')
    flushBeforeLeave()
    return navigateTo(externalLessonUrl, { external: true, replace: true })
  }

  const optionsReady = await useMiddlewareOptionsReady()

  if (!optionsReady) {
    console.warn('options are unavailable, falling back to the external lesson room')
    return toExternalLesson()
  }

  if (!optionsStore.isClassroomInCs) {
    return toExternalLesson()
  }

  // From here the classroom_in_cs flag is known ON: main-app bounces every kid to this
  // page, so redirecting back to lesson_url from any branch below is a guaranteed
  // infinite loop — non-web voip is handled in place instead.
  const voip = await useMiddlewareBookingVoip(booking)

  if (!voip) {
    console.warn('unable to resolve voip for middleware/voip-room.ts')
    useToast().error(LESSON_ROOM_UNAVAILABLE)

    return navigateTo('/')
  }

  voipRoomStore.setBookingVoip(voip)

  if (voip !== Voip.Web) {
    return
  }

  const { fetchRoomToken } = useVoipRoomTokenApi()
  const result = await fetchRoomToken(bookingId)

  // Room not ready = the tutor hasn't opened it yet, not a failure: abort the redirect so
  // the join waiting modal stays open — its booking watcher retries on the next ping/WS tick.
  const isDirectEntry = to.fullPath === from.fullPath

  if (result.notReady) {
    return isDirectEntry ? navigateTo('/') : abortNavigation()
  }

  if (result.error) {
    useToast().error({
      title: 'Error',
      description: result.error.message,
    })

    return navigateTo('/')
  }

  if (result.superseded) {
    return
  }

  if (!voipRoomStore.isLiveKitRoom) {
    console.warn('unexpected voip room provider for middleware/voip-room.ts', voipRoomStore.getRoomData?.provider)
    useToast().error(LESSON_ROOM_UNAVAILABLE)

    return navigateTo('/')
  }
})
